import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useApp } from '../context/AppContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { formatDistanceToNow } from 'date-fns';
import { verifySmfProximity } from '../services/geoService';

const LOTS = ['Terminal A Garage', 'Terminal B Garage', 'Daily Lot', 'Economy Lot'];
const STATUSES = ['Open', 'Moderate', 'Busy', 'Full'];

export default function Parking() {
  const { parkingReports, submitParkingReport, addNotification } = useApp();
  const [showReportForm, setShowReportForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ lot: '', status: 'Moderate', level: '', handicapAvailable: false });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.lot) return;

    setLoading(true);
    try {
      const { verified, distance } = await verifySmfProximity(10);
      
      if (!verified) {
        addNotification(`Integrity Check Failed: You are ${distance.toFixed(1)} miles from SMF. Verification required at airport.`, 'error');
        setLoading(false);
        return;
      }

      await submitParkingReport(formData);
      setShowReportForm(false);
      setFormData({ lot: '', status: 'Moderate', level: '', handicapAvailable: false });
      addNotification('Parking intel verified!', 'success');
    } catch (err) {
      addNotification(err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Open': return 'text-emerald-400 bg-emerald-400/10';
      case 'Moderate': return 'text-blue-400 bg-blue-400/10';
      case 'Busy': return 'text-amber-400 bg-amber-400/10';
      case 'Full': return 'text-rose-400 bg-rose-400/10';
      default: return 'text-slate-400 bg-slate-400/10';
    }
  };

  return (
    <div className="p-4 pb-24 md:pb-8 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-white">Parking Intelligence</h2>
          <p className="text-slate-400 text-sm">Real-time lot availability at SMF</p>
        </div>
        <button onClick={() => setShowReportForm(!showReportForm)} className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 text-sm">
          <SafeIcon icon={showReportForm ? FiIcons.FiX : FiIcons.FiPlus} />
          <span>{showReportForm ? 'Cancel' : 'Report'}</span>
        </button>
      </div>

      <AnimatePresence>
        {showReportForm && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
            <form onSubmit={handleSubmit} className="bg-slate-850 border border-amber-500/30 rounded-xl p-4 space-y-4 shadow-xl">
              <div className="bg-blue-500/10 border border-blue-500/20 p-2 rounded-lg text-center mb-2">
                <p className="text-[10px] text-blue-300 font-bold uppercase tracking-wider">Verification Required within 10 miles of SMF</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-slate-400 uppercase">Select Lot</label>
                  <select required className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2.5 text-white text-sm outline-none" value={formData.lot} onChange={e => setFormData({ ...formData, lot: e.target.value })}>
                    <option value="" disabled>Select...</option>
                    {LOTS.map(l => <option key={l} value={l}>{l}</option>)}
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-slate-400 uppercase">Availability</label>
                  <select className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2.5 text-white text-sm outline-none" value={formData.status} onChange={e => setFormData({ ...formData, status: e.target.value })}>
                    {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-slate-400 uppercase">Floor / Level</label>
                  <input type="text" placeholder="e.g. Level 4" className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2.5 text-white text-sm outline-none" value={formData.level} onChange={e => setFormData({ ...formData, level: e.target.value })} />
                </div>
                <div className="flex flex-col justify-center">
                  <label className="text-xs font-semibold text-slate-400 uppercase mb-2">Handicap Slots?</label>
                  <button type="button" onClick={() => setFormData({ ...formData, handicapAvailable: !formData.handicapAvailable })} className={`flex items-center justify-center space-x-2 py-2 rounded-lg border transition-all ${formData.handicapAvailable ? 'bg-blue-500/20 border-blue-500 text-blue-400' : 'bg-slate-900 border-slate-700 text-slate-500'}`}>
                    <SafeIcon icon={FiIcons.FiUser} />
                    <span className="text-xs font-bold">Open Slots</span>
                  </button>
                </div>
              </div>
              <button type="submit" disabled={loading} className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold py-3 rounded-lg transition-all flex items-center justify-center space-x-2">
                {loading ? <SafeIcon icon={FiIcons.FiLoader} className="animate-spin" /> : <SafeIcon icon={FiIcons.FiSend} />}
                <span>Verify & Submit (+15 XP)</span>
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="space-y-4">
        {parkingReports.map((report, idx) => (
          <motion.div key={report.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.1 }} className="bg-slate-850 border border-slate-800 rounded-xl p-4 overflow-hidden relative">
            <div className={`absolute top-0 right-0 px-3 py-1 text-[10px] font-bold uppercase rounded-bl-lg ${getStatusColor(report.status)}`}>
              {report.status}
            </div>
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-white font-bold text-lg">{report.lot}</h3>
                <div className="flex items-center text-slate-400 text-xs mt-1">
                  <SafeIcon icon={FiIcons.FiMapPin} className="mr-1.5 text-amber-500" /> {report.level || 'General Area'}
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between border-t border-slate-800 pt-3 text-[10px] text-slate-500">
               <span>Verified via GPS Proximity</span>
               <span className="font-mono">{formatDistanceToNow(new Date(report.timestamp), { addSuffix: true })}</span>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}